import React, { useRef, useState, useEffect } from 'react';
import { Pen, Trash2, Save, X } from 'lucide-react';

const SignaturePad = ({ label, name, value, onChange, required = false }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [ctx, setCtx] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext('2d');
      context.strokeStyle = '#000';
      context.lineWidth = 2;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      setCtx(context);
      
      // Set canvas size
      resizeCanvas();
      
      // Load existing signature if available
      if (value) {
        loadSignature(value);
      }
    }

    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  useEffect(() => {
    if (value && ctx) {
      loadSignature(value);
    }
  }, [value, ctx]);

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const container = canvas.parentElement;
      const tempDrawing = canvas.toDataURL(); // Save current drawing
      
      canvas.width = container.clientWidth - 32;
      canvas.height = 150;
      
      const context = canvas.getContext('2d');
      context.strokeStyle = '#000';
      context.lineWidth = 2;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      setCtx(context);
      
      // Restore drawing if exists
      if (tempDrawing && tempDrawing !== 'data:,') {
        const img = new Image();
        img.onload = () => {
          context.drawImage(img, 0, 0);
          setHasSignature(true);
        };
        img.src = tempDrawing;
      }
    }
  };

  const loadSignature = (signatureData) => {
    if (signatureData && ctx) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.drawImage(img, 0, 0, canvasRef.current.width, canvasRef.current.height);
        setHasSignature(true);
      };
      img.src = signatureData;
    }
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    ctx.closePath();
    
    // Check if canvas has any drawing
    const canvas = canvasRef.current;
    const pixelData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    const hasDrawing = pixelData.some(value => value !== 0);
    setHasSignature(hasDrawing);
    
    // Save signature data
    if (hasDrawing) {
      const signatureData = canvas.toDataURL();
      onChange({ target: { name, value: signatureData } });
    }
  };

  const clearSignature = () => {
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasSignature(false);
    onChange({ target: { name, value: '' } });
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    startDrawing(touch);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    draw(touch);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="border border-gray-300 rounded-lg bg-white">
        {/* Canvas for drawing */}
        <div className="relative">
          <canvas
            ref={canvasRef}
            className="w-full h-[150px] cursor-crosshair touch-none"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={stopDrawing}
            onTouchCancel={stopDrawing}
          />
          
          {/* Placeholder text when empty */}
          {!hasSignature && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-gray-400">
              <Pen className="w-5 h-5 mr-2" />
              <span>Draw your signature here</span>
            </div>
          )}
        </div>
        
        {/* Controls */}
        <div className="flex items-center justify-between p-2 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500">
            {hasSignature ? 'Signature captured' : 'No signature'}
          </div>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={clearSignature}
              className="flex items-center px-2 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Clear
            </button>
          </div>
        </div>
      </div>
      
      {/* Hidden input to store signature data */}
      <input type="hidden" name={name} value={value || ''} />
    </div>
  );
};

export default SignaturePad;